import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Building2, MapPin, FileText, Image, ChevronRight,
  ChevronLeft, Check, Upload, X, Info, Users,
  Wifi, Utensils, Wind, Car, Waves, Star
} from "lucide-react";
import Navbar from "../../components/Header";
import axios from "axios";

const G      = "#3A9D23";
const G_DARK = "#2f7d1c";
const G_LIGHT = "#e8f5e2";
const API    = "https://api.komorotel.com";

const STEPS = [
  { icon: <Building2 size={18} />, label: "Type & île"     },
  { icon: <MapPin size={18} />,    label: "Localisation"   },
  { icon: <FileText size={18} />,  label: "Description"    },
  { icon: <Star size={18} />,      label: "Équipements"    },
  { icon: <Check size={18} />,     label: "Confirmation"   },
];

const TYPES = [
  { value: "hotel",              label: "Hôtel",               desc: "Établissement avec réception et services",     icon: "🏨" },
  { value: "chambre_hote",       label: "Chambre d'hôte",      desc: "Chambre chez l'habitant avec accueil personnalisé", icon: "🏡" },
  { value: "appartement_studio", label: "Appartement / Studio", desc: "Logement indépendant avec équipements",        icon: "🏢" },
];

const ISLANDS = [
  { value: "Ngazidja", label: "Ngazidja (Grande Comore)", capital: "Moroni"    },
  { value: "Anjouan",  label: "Anjouan",                  capital: "Mutsamudu" },
  { value: "Moheli",   label: "Mohéli",                   capital: "Fomboni"   },
];

const EQUIPMENTS = [
  { value: "wifi",          label: "Wi-Fi gratuit",    icon: <Wifi size={16} />     },
  { value: "restaurant",    label: "Restaurant",        icon: <Utensils size={16} /> },
  { value: "piscine",       label: "Piscine",           icon: <Waves size={16} />    },
  { value: "climatisation", label: "Climatisation",     icon: <Wind size={16} />     },
  { value: "parking",       label: "Parking gratuit",   icon: <Car size={16} />      },
  { value: "plage",         label: "Plage privée",      icon: <Waves size={16} />    },
  { value: "navette",       label: "Navette aéroport",  icon: <Car size={16} />      },
  { value: "petit-dej",     label: "Petit-déjeuner",    icon: <Utensils size={16} /> },
];

/* ── Input stylé ── */
function Input({ label, error, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      {label && <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>{label}</label>}
      <input
        {...props}
        style={{
          width: "100%", border: `1.5px solid ${error ? "#ef4444" : focused ? G : "#e5e7eb"}`,
          borderRadius: 12, padding: "11px 14px", fontSize: 14, outline: "none",
          fontFamily: "inherit", color: "#111827", background: "#fff",
          boxSizing: "border-box", transition: "border-color 0.2s",
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
      {error && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 3 }}>{error}</div>}
    </div>
  );
}

function Textarea({ label, error, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      {label && <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>{label}</label>}
      <textarea
        {...props}
        style={{
          width: "100%", border: `1.5px solid ${error ? "#ef4444" : focused ? G : "#e5e7eb"}`,
          borderRadius: 12, padding: "11px 14px", fontSize: 14, outline: "none",
          fontFamily: "inherit", color: "#111827", background: "#fff",
          boxSizing: "border-box", transition: "border-color 0.2s", resize: "vertical", lineHeight: 1.6,
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
      {error && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 3 }}>{error}</div>}
    </div>
  );
}

/* ── Barre d'étapes ── */
function StepBar({ current }) {
  return (
    <div style={{ display: "flex", alignItems: "center", marginBottom: 40 }}>
      {STEPS.map((step, i) => (
        <React.Fragment key={i}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            <div style={{
              width: 38, height: 38, borderRadius: "50%",
              background: i < current ? G : i === current ? G : "#f3f4f6",
              border: `2px solid ${i <= current ? G : "#e5e7eb"}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: i <= current ? "#fff" : "#9ca3af",
              transition: "all 0.3s",
            }}>
              {i < current ? <Check size={16} strokeWidth={2.5} /> : step.icon}
            </div>
            <span style={{ fontSize: 11, fontWeight: i === current ? 700 : 400, color: i === current ? G : i < current ? "#374151" : "#9ca3af", whiteSpace: "nowrap" }}>
              {step.label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div style={{ flex: 1, height: 2, background: i < current ? G : "#e5e7eb", margin: "0 8px", marginBottom: 22, transition: "background 0.3s" }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────
   PAGE PRINCIPALE
───────────────────────────────────────────────── */
export default function RegisterHotel() {
  const navigate = useNavigate();
  const [step,   setStep]   = useState(0);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    type:        "",
    island:      "",
    name:        "",
    city:        "",
    adress:      "",
    nif:         "",
    email:       "",
    desc:        "",
    equipments:  [],
    photos:      [],
  });

  const set = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    if (errors[k]) setErrors(p => ({ ...p, [k]: "" }));
  };

  const toggleEquip = (val) => {
    setForm(p => ({
      ...p,
      equipments: p.equipments.includes(val)
        ? p.equipments.filter(e => e !== val)
        : [...p.equipments, val],
    }));
  };

  /* ── Validation par étape ── */
  const validate = () => {
    const e = {};
    if (step === 0) {
      if (!form.type)   e.type   = "Sélectionnez un type";
      if (!form.island) e.island = "Sélectionnez une île";
    }
    if (step === 1) {
      if (!form.name.trim())   e.name   = "Requis";
      if (!form.city.trim())   e.city   = "Requis";
      if (!form.adress.trim()) e.adress = "Requis";
      if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Email invalide";
    }
    if (step === 2) {
      if (!form.desc.trim() || form.desc.length < 50) e.desc = "Description trop courte (50 caractères min.)";
    }
    return e;
  };

  const next = () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setStep(s => s + 1);
    window.scrollTo(0, 0);
  };

  const prev = () => { setStep(s => s - 1); window.scrollTo(0, 0); };

  /* ── Submit final ── */
  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/hotels`, form, { withCredentials: true });
      setStep(4); // étape confirmation
    } catch (err) {
      setErrors({ api: err.response?.data?.message || "Une erreur est survenue" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f9fafb", fontFamily: "'DM Sans','Inter',system-ui,sans-serif" }}>
      <Navbar />

      {/* Hero */}
      <div style={{ background: "linear-gradient(135deg, #0a2810 0%, #0f4020 100%)", paddingTop: 88, paddingBottom: 48 }}>
        <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 32px", textAlign: "center" }}>
          <div style={{ display: "inline-flex", alignItems: "center", background: "rgba(58,157,35,0.25)", border: "1px solid rgba(58,157,35,0.4)", color: G, fontSize: 11, fontWeight: 800, letterSpacing: "0.09em", padding: "5px 14px", borderRadius: 20, marginBottom: 18 }}>
            🏨 HÔTELIERS
          </div>
          <h1 style={{ fontFamily: "Georgia, serif", fontSize: 36, fontWeight: 700, color: "#fff", lineHeight: 1.2, marginBottom: 12 }}>
            Proposez votre établissement
          </h1>
          <p style={{ fontSize: 14, color: "rgba(255,255,255,0.7)", lineHeight: 1.7, maxWidth: 480, margin: "0 auto" }}>
            Rejoignez Komorotel et accueillez plus de voyageurs. Commission de 5% seulement sur les réservations confirmées.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 800, margin: "0 auto", padding: "40px 32px 64px" }}>

        {/* Étapes */}
        {step < 4 && <StepBar current={step} />}

        {/* ── ÉTAPE 0 — Type & Île ── */}
        {step === 0 && (
          <div style={{ background: "#fff", borderRadius: 20, border: "1px solid #f3f4f6", padding: "32px", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
            <h2 style={{ fontFamily: "Georgia, serif", fontSize: 20, fontWeight: 700, color: "#111827", marginBottom: 6 }}>Type d'établissement</h2>
            <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 24 }}>Quel type d'hébergement proposez-vous ?</p>

            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 32 }}>
              {TYPES.map(t => (
                <div key={t.value}
                  onClick={() => set("type", t.value)}
                  style={{
                    display: "flex", alignItems: "center", gap: 16, padding: "16px 20px",
                    border: `2px solid ${form.type === t.value ? G : "#f3f4f6"}`,
                    borderRadius: 14, cursor: "pointer",
                    background: form.type === t.value ? G_LIGHT : "#fff",
                    transition: "all 0.18s",
                  }}>
                  <span style={{ fontSize: 28 }}>{t.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{t.label}</div>
                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{t.desc}</div>
                  </div>
                  <div style={{
                    width: 22, height: 22, borderRadius: "50%",
                    border: `2px solid ${form.type === t.value ? G : "#d1d5db"}`,
                    background: form.type === t.value ? G : "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, transition: "all 0.18s",
                  }}>
                    {form.type === t.value && <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#fff" }} />}
                  </div>
                </div>
              ))}
            </div>
            {errors.type && <div style={{ fontSize: 12, color: "#ef4444", marginBottom: 16 }}>{errors.type}</div>}

            <h2 style={{ fontFamily: "Georgia, serif", fontSize: 20, fontWeight: 700, color: "#111827", marginBottom: 6 }}>Île</h2>
            <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 18 }}>Sur quelle île se situe votre établissement ?</p>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
              {ISLANDS.map(isl => (
                <div key={isl.value}
                  onClick={() => set("island", isl.value)}
                  style={{
                    padding: "16px 14px", border: `2px solid ${form.island === isl.value ? G : "#f3f4f6"}`,
                    borderRadius: 14, cursor: "pointer", textAlign: "center",
                    background: form.island === isl.value ? G_LIGHT : "#fff",
                    transition: "all 0.18s",
                  }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: form.island === isl.value ? G : "#111827" }}>{isl.value}</div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>{isl.capital}</div>
                </div>
              ))}
            </div>
            {errors.island && <div style={{ fontSize: 12, color: "#ef4444", marginTop: 8 }}>{errors.island}</div>}
          </div>
        )}

        {/* ── ÉTAPE 1 — Localisation ── */}
        {step === 1 && (
          <div style={{ background: "#fff", borderRadius: 20, border: "1px solid #f3f4f6", padding: "32px", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
            <h2 style={{ fontFamily: "Georgia, serif", fontSize: 20, fontWeight: 700, color: "#111827", marginBottom: 6 }}>Informations générales</h2>
            <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 28 }}>Les informations de base sur votre établissement.</p>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <Input label="Nom de l'établissement *" value={form.name} onChange={e => set("name", e.target.value)} placeholder="ex: Hotel Itsandra Beach" error={errors.name} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <Input label="Ville *" value={form.city} onChange={e => set("city", e.target.value)} placeholder="ex: Moroni" error={errors.city} />
                <Input label="NIF (optionnel)" value={form.nif} onChange={e => set("nif", e.target.value)} placeholder="Numéro fiscal" />
              </div>
              <Input label="Adresse complète *" value={form.adress} onChange={e => set("adress", e.target.value)} placeholder="Rue, quartier..." error={errors.adress} />
              <Input label="Email de contact *" type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="contact@monhotel.com" error={errors.email} />
            </div>

            <div style={{ marginTop: 20, background: "#f9fafb", borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: 10 }}>
              <Info size={15} color="#9ca3af" style={{ marginTop: 1, flexShrink: 0 }} />
              <p style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.6, margin: 0 }}>
                Ces informations seront visibles sur votre fiche établissement. Votre email ne sera jamais partagé publiquement.
              </p>
            </div>
          </div>
        )}

        {/* ── ÉTAPE 2 — Description ── */}
        {step === 2 && (
          <div style={{ background: "#fff", borderRadius: 20, border: "1px solid #f3f4f6", padding: "32px", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
            <h2 style={{ fontFamily: "Georgia, serif", fontSize: 20, fontWeight: 700, color: "#111827", marginBottom: 6 }}>Description</h2>
            <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 24 }}>
              Décrivez votre établissement pour attirer les voyageurs. Soyez précis et mettez en avant ce qui vous rend unique.
            </p>
            <Textarea
              label="Description de l'établissement *"
              value={form.desc}
              onChange={e => set("desc", e.target.value)}
              placeholder="Décrivez votre établissement : cadre, services, ambiance, points forts..."
              rows={8}
              error={errors.desc}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
              <span style={{ fontSize: 11, color: form.desc.length >= 50 ? G : "#9ca3af" }}>
                {form.desc.length} / 50 min.
              </span>
            </div>

            {/* Conseils rédaction */}
            <div style={{ marginTop: 20, background: G_LIGHT, borderRadius: 12, padding: "16px 18px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: G, marginBottom: 10 }}>💡 Conseils pour une bonne description</div>
              {["Mentionnez l'emplacement et les attractions proches", "Décrivez les services inclus (piscine, restaurant, etc.)", "Parlez de l'ambiance et du type de clientèle", "Indiquez ce qui vous différencie des autres établissements"].map((tip, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                  <Check size={12} color={G} style={{ marginTop: 2, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: "#374151" }}>{tip}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── ÉTAPE 3 — Équipements ── */}
        {step === 3 && (
          <div style={{ background: "#fff", borderRadius: 20, border: "1px solid #f3f4f6", padding: "32px", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
            <h2 style={{ fontFamily: "Georgia, serif", fontSize: 20, fontWeight: 700, color: "#111827", marginBottom: 6 }}>Équipements & services</h2>
            <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 24 }}>Sélectionnez les équipements disponibles dans votre établissement.</p>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginBottom: 32 }}>
              {EQUIPMENTS.map(eq => {
                const selected = form.equipments.includes(eq.value);
                return (
                  <div key={eq.value}
                    onClick={() => toggleEquip(eq.value)}
                    style={{
                      display: "flex", alignItems: "center", gap: 12, padding: "13px 16px",
                      border: `2px solid ${selected ? G : "#f3f4f6"}`,
                      borderRadius: 12, cursor: "pointer",
                      background: selected ? G_LIGHT : "#fff",
                      transition: "all 0.18s",
                    }}>
                    <div style={{ color: selected ? G : "#9ca3af", transition: "color 0.18s" }}>{eq.icon}</div>
                    <span style={{ fontSize: 13, fontWeight: 500, color: selected ? G : "#374151", flex: 1 }}>{eq.label}</span>
                    <div style={{
                      width: 20, height: 20, borderRadius: 6,
                      border: `2px solid ${selected ? G : "#d1d5db"}`,
                      background: selected ? G : "#fff",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "all 0.18s", flexShrink: 0,
                    }}>
                      {selected && <Check size={11} color="#fff" strokeWidth={3} />}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Résumé */}
            <div style={{ background: "#f9fafb", borderRadius: 14, padding: "20px 22px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 12 }}>Récapitulatif de votre établissement</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {[
                  { label: "Type",         val: TYPES.find(t => t.value === form.type)?.label || "—" },
                  { label: "Île",          val: form.island || "—" },
                  { label: "Nom",          val: form.name || "—" },
                  { label: "Ville",        val: form.city || "—" },
                  { label: "Équipements",  val: `${form.equipments.length} sélectionné${form.equipments.length > 1 ? "s" : ""}` },
                ].map((r, i) => (
                  <div key={i}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>{r.label}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{r.val}</div>
                  </div>
                ))}
              </div>
            </div>

            {errors.api && (
              <div style={{ marginTop: 16, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "12px 16px", fontSize: 13, color: "#ef4444" }}>
                ⚠️ {errors.api}
              </div>
            )}
          </div>
        )}

        {/* ── ÉTAPE 4 — Confirmation ── */}
        {step === 4 && (
          <div style={{ background: "#fff", borderRadius: 20, border: "1px solid #f3f4f6", padding: "48px 32px", boxShadow: "0 2px 12px rgba(0,0,0,0.04)", textAlign: "center" }}>
            <div style={{ width: 72, height: 72, borderRadius: "50%", background: G_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}>
              <Check size={32} color={G} strokeWidth={2.5} />
            </div>
            <h2 style={{ fontFamily: "Georgia, serif", fontSize: 26, fontWeight: 700, color: "#111827", marginBottom: 10 }}>
              Établissement soumis !
            </h2>
            <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.75, maxWidth: 440, margin: "0 auto 32px" }}>
              Votre demande a bien été reçue. Notre équipe va examiner votre dossier et vous contactera à <strong>{form.email}</strong> dans les 48 heures.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <button onClick={() => navigate("/")}
                style={{ background: G, color: "#fff", border: "none", borderRadius: 12, padding: "12px 28px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = G_DARK}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = G}>
                Retour à l'accueil
              </button>
              <button onClick={() => { setStep(0); setForm({ type: "", island: "", name: "", city: "", adress: "", nif: "", email: "", desc: "", equipments: [], photos: [] }); }}
                style={{ background: G_LIGHT, color: G, border: "none", borderRadius: 12, padding: "12px 24px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                Soumettre un autre établissement
              </button>
            </div>
          </div>
        )}

        {/* ── Navigation boutons ── */}
        {step < 4 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 24 }}>
            <button onClick={step === 0 ? () => navigate("/") : prev}
              style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 18px", fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer" }}>
              <ChevronLeft size={16} /> {step === 0 ? "Annuler" : "Retour"}
            </button>

            {step < 3 ? (
              <button onClick={next}
                style={{ display: "flex", alignItems: "center", gap: 6, background: G, color: "#fff", border: "none", borderRadius: 10, padding: "10px 22px", fontSize: 13, fontWeight: 700, cursor: "pointer", transition: "background 0.18s" }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = G_DARK}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = G}>
                Continuer <ChevronRight size={16} />
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={loading}
                style={{ display: "flex", alignItems: "center", gap: 6, background: loading ? "#d1d5db" : G, color: "#fff", border: "none", borderRadius: 10, padding: "10px 22px", fontSize: 13, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", transition: "background 0.18s" }}
                onMouseEnter={e => { if (!loading) e.currentTarget.style.backgroundColor = G_DARK; }}
                onMouseLeave={e => { if (!loading) e.currentTarget.style.backgroundColor = G; }}>
                {loading ? "Envoi..." : <><Check size={15} /> Soumettre</>}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}